//----------------------------------------------------------------------------------------------------------------------
// Audio Engine
//----------------------------------------------------------------------------------------------------------------------

import {
    type AudioBus,
    type AudioEngineV2,
    CreateAudioEngineAsync,
    type IStaticSoundOptions,
    type MainAudioBus,
    type PrimaryAudioBus,
    type StaticSound,
} from '@babylonjs/core';

// Interfaces
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Utils
import type { LoggingUtility } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

export class AudioEngine implements Disposable
{
    private _engine : AudioEngineV2 | null = null;
    private _mainBus : MainAudioBus | null = null;
    private _buses = new Map<string, AudioBus>();
    private _log : LoggerInterface;

    constructor(logger : LoggingUtility)
    {
        this._log = logger.getLogger('AudioEngine');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    async initialize() : Promise<void>
    {
        this._log.info('Initializing audio engine...');

        this._engine = await CreateAudioEngineAsync();
        this._mainBus = await this._engine.createMainBusAsync('main');

        this._log.info('Audio engine initialized.');
    }

    async $teardown() : Promise<void>
    {
        this._log.info('Tearing down audio engine...');

        this._buses.clear();
        this._mainBus = null;

        if(this._engine)
        {
            this._engine.dispose();
            this._engine = null;
        }

        this._log.info('Audio engine torn down.');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Bus Management
    //------------------------------------------------------------------------------------------------------------------

    async createBus(name : string) : Promise<AudioBus>
    {
        if(!this._engine || !this._mainBus)
        {
            throw new Error('AudioEngine not initialized');
        }

        this._log.debug(`Creating audio bus: ${ name }`);
        const bus = await this._engine.createBusAsync(name, { outBus: this._mainBus });
        this._buses.set(name, bus);
        return bus;
    }

    getBus(name : string) : AudioBus | undefined
    {
        return this._buses.get(name);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Sound Creation
    //------------------------------------------------------------------------------------------------------------------

    async createSound(
        name : string,
        url : string,
        bus ?: AudioBus,
        options ?: Partial<IStaticSoundOptions>
    ) : Promise<StaticSound>
    {
        if(!this._engine)
        {
            throw new Error('AudioEngine not initialized');
        }

        const outBus : PrimaryAudioBus | undefined = bus ?? this._mainBus ?? undefined;

        // Spread user options and inject outBus; the cast is needed because
        // IStaticSoundOptions.outBus accepts Nullable<PrimaryAudioBus>
        const soundOptions = {
            ...options,
            ...(outBus ? { outBus } : {}),
        } as Partial<IStaticSoundOptions>;

        return this._engine.createSoundAsync(name, url, soundOptions);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Volume
    //------------------------------------------------------------------------------------------------------------------

    setMasterVolume(volume : number) : void
    {
        if(!this._engine)
        {
            throw new Error('AudioEngine not initialized');
        }

        this._engine.volume = volume;
    }

    getMasterVolume() : number
    {
        if(!this._engine)
        {
            return 1;
        }

        return this._engine.volume;
    }

    setBusVolume(bus : AudioBus, volume : number) : void
    {
        bus.volume = volume;
    }
}

//----------------------------------------------------------------------------------------------------------------------
